import { SupportedLocale } from '../hooks/useLanguage';

export type LocaleContent = {
  hero: {
    eyebrow: string;
    title: string;
    subtitle: string;
    description: string;
  };
  waitlist: {
    placeholder: string;
    submit: string;
    success: string;
    error: string;
    label: string;
  };
  likeThanks: string;
  ctaSecondary: string;
  languageToggleLabel: string;
  featureDetailLabel: string;
  status: {
    before: string;
    enhancing: string;
    ready: string;
    enhancingMessage: string;
    enhancedMessage: string;
    enhanceButton: string;
    afterLabel: string;
  };
  features: Array<{ title: string; description: string }>;
  detailedFeatures: Array<{ title: string; icon: string; description: string; detail: string }>;
  typewriter: string;
  samplePrompt: string;
  sections: {
    howItWorksEyebrow: string;
    howItWorksTitle: string;
    howItWorksDescription: string;
    storyEyebrow: string;
    storyTitle: string;
    storyDescription: string;
    productEyebrow: string;
    productTitle: string;
    productDescription: string;
    clipboardTitle: string;
    clipboardDescription: string;
    finalEyebrow: string;
    finalTitle: string;
    finalDescription: string;
  };
  steps: Array<{ title: string; body: string }>;
  navigation: {
    links: Array<{ label: string; href: string }>;
    downloadLabel: string;
  };
  footer: {
    description: string;
    productHeading: string;
    companyHeading: string;
    note: string;
    productLinks: Array<{ label: string; href: string }>;
    companyLinks: Array<{ label: string; href: string }>;
  };
};

const en: LocaleContent = {
  hero: {
    eyebrow: 'Thoughtful by design',
    title: 'Clarity for every AI interaction.',
    subtitle:
      'Promptilia is a precision prompt enhancer for macOS. Select anything, tap a shortcut, and receive a calmer, clearer version of what you meant.',
    description:
      'Every rewrite honors your Profiles and Rules, so Gmail replies, Jira tickets, and design briefs share the same tone, structure, and clarity—without leaving your Mac.',
  },
  waitlist: {
    placeholder: 'you@email.com',
    submit: 'Join the waitlist',
    success: 'Thanks! We will be in touch soon.',
    error: 'Enter a valid email to join the list.',
    label: 'Email address',
  },
  likeThanks: 'Thanks for the love',
  ctaSecondary: 'Like Promptilia',
  languageToggleLabel: 'Select language',
  featureDetailLabel: 'More about {title}',
  status: {
    before: 'Before',
    enhancing: 'Enhancing…',
    ready: 'Ready',
    enhancingMessage: 'Enhancing prompt and merging profiles and rules…',
    enhancedMessage: 'Enhanced prompt ready',
    enhanceButton: 'Enhance Prompt',
    afterLabel: 'After Promptilia',
  },
  features: [
    {
      title: 'Gentle structure',
      description: 'Clean, composed, and thoughtful—from raw text to a calm brief.',
    },
    {
      title: 'Local trust',
      description: 'Everything runs on your Mac. No servers. No logging. Tuned for clarity.',
    },
    {
      title: 'Quiet speed',
      description: 'One shortcut gathers, enhances, and hands back a steady answer.',
    },
  ],
  detailedFeatures: [
    {
      title: 'Clarity Engine',
      icon: '✨',
      description: 'Transforms any selected text into a structured, high-signal prompt.',
      detail:
        'Automatically cleans up messy paragraphs, adds structure, and keeps tone calm so every AI prompt reads like you meant it the first time.',
    },
    {
      title: 'Profiles',
      icon: '🪄',
      description: 'Define tone, style, and behavioral presets for every rewrite.',
      detail:
        'Capture your personal voice—warm brief, product notes, executive updates—and reuse it instantly across every prompt.',
    },
    {
      title: 'Rules',
      icon: '📐',
      description: 'Keep outputs consistent with strict formatting and structural constraints.',
      detail:
        'Set bullet expectations, headers, summarization requirements, or character counts so each prompt stays compliant.',
    },
    {
      title: 'Spells',
      icon: '🎯',
      description: 'Reusable micro-prompts for common edits, summaries, or transformations.',
      detail:
        'Build a palette of tiny automations—rewrite softer, summarize for exec, highlight risks—and trigger them on demand.',
    },
    {
      title: 'Context Awareness',
      icon: '🧠',
      description: 'Adapts tone, structure, and formatting to match the app you are using.',
      detail:
        'Promptilia senses whether you’re drafting email, Jira tickets, or notes and aligns the rewrite accordingly.',
    },
    {
      title: 'Local Privacy',
      icon: '🔒',
      description: 'Processing and storage stay local—no accounts, no servers, no cloud.',
      detail:
        'Your prompts, profiles, and history never leave the machine, keeping sensitive work contained and auditable.',
    },
    {
      title: 'Clipboard Workflow',
      icon: '⌘',
      description: 'Capture, enhance, and paste improved text with a single shortcut.',
      detail:
        'Select, press the shortcut, and paste—Promptilia hands back the refined copy in one motion.',
    },
    {
      title: 'History',
      icon: '🗂️',
      description: 'Browse, reuse, or iterate on every enhanced prompt you’ve created.',
      detail:
        'Scroll through previous rewrites, duplicate, or re-run them with new context whenever you need a starting point.',
    },
  ],
  typewriter: `## Detailed and enhanced user request\nThe current global rules section in promptilia appears disorganized. I would like it to be reformatted to align with the structure of the profiles section. Additionally, each profile should contain its own nested section for better organization. Furthermore, in the settings screen, I want to introduce an option that allows users to customize the keyboard shortcuts for input and paste actions, including the addition of macOS icons for the modifier keys to enhance usability.\n\n## Tasks\n- Reformat the global rules section to match the profiles section structure.\n- Create nested sections for each profile within the global rules.\n- Add a user setting for customizing input and paste keyboard shortcuts.\n- Incorporate macOS icons for modifier keys in the settings screen.\n- Ensure the new layout is user-friendly and accessible.\n- Test the implementation for functionality and usability.\n\n## Summary\nRole: A expert SE eng principles aligning with product, UX, design. solutions are arch-sound, secure, privacy-safe, UX-focused. responsibility for data safety in modern systems. | Goal: Refine the global rules section and enhance the settings screen for user input customization. | Instructions: Maintain clarity and consistency in formatting.; ✓ all sections are logically organized and easy to navigate.; + visual elements like icons to enhance user experience.; ✓ all user inputs and provide feedback on errors.; 📝 any assumptions and constraints clearly.`,
  samplePrompt:
    "This is Promptilia and it has a 'global rules' section but it still feels messy. I want that section to follow the same structure as Profiles. Each profile should also get its own nested section. On the settings screen I want to add an option so people can change the capture and paste shortcuts, and it needs to show the macOS modifier icons.",
  sections: {
    howItWorksEyebrow: 'How it works',
    howItWorksTitle: 'A macOS companion that runs everywhere.',
    howItWorksDescription: 'Promptilia lives on your Mac. Select any text in any app, press your shortcut, and it captures, enhances, and pastes a thoughtful rewrite using your Profiles and Rules.',
    storyEyebrow: 'How it works',
    storyTitle: 'Why Promptilia keeps macOS prompts consistent',
    storyDescription:
      'Promptilia stays always-on as a macOS app. Select any text—email, brief, or ticket—press the shortcut, and Promptilia captures it, merges your active profile and rule set, and returns a calmer, keyword-rich prompt without switching apps or losing flow.',
    productEyebrow: 'Product',
    productTitle: 'Designed for focus.',
    productDescription:
      'These are the essentials that keep Promptilia steady, warm, and structured: Profiles to define tone, Rules to govern delivery, clipboard capture for speed, and local privacy for compliance.',
    clipboardTitle: 'Promptilia is coming soon.',
    clipboardDescription: 'Join the waitlist to hear when the first macOS build ships.',
    finalEyebrow: 'How it works',
    finalTitle: 'A macOS companion that runs everywhere.',
    finalDescription: 'Promptilia lives on your Mac. Select, enhance, paste, repeat.',
  },
  steps: [
    {
      title: '1. Capture',
      body: 'Highlight any text across macOS and press your shortcut. Promptilia instantly captures what you selected without switching apps.',
    },
    {
      title: '2. Transform',
      body: 'Promptilia runs your Profiles and Rules—tone, structure, formatting—through AI to reshape the text to your standards.',
    },
    {
      title: '3. Return',
      body: 'Within seconds you get a polished, structured variant ready to paste, replace, or keep refining.',
    },
  ],
  navigation: {
    links: [
      { label: 'Features', href: '/features' },
      { label: 'Pricing', href: '/pricing' },
      { label: 'About', href: '/about' },
      { label: 'Contact', href: '/contact' },
    ],
    downloadLabel: 'Download',
  },
  footer: {
    description:
      'Transform your prompts with AI-powered enhancement, intelligent management, and seamless workflow integration. Built for developers, by developers.',
    productHeading: 'Product',
    companyHeading: 'Company',
    note: '© 2025 Promptilia. All rights reserved.',
    productLinks: [
      { label: 'Features', href: '/features' },
      { label: 'Pricing', href: '/pricing' },
      { label: 'Download', href: '#' },
    ],
    companyLinks: [
      { label: 'About', href: '/about' },
      { label: 'Contact', href: '/contact' },
      { label: 'Privacy', href: '#' },
      { label: 'Terms', href: '#' },
    ],
  },
};

const ptBr: LocaleContent = {
  hero: {
    eyebrow: 'Pensado com cuidado',
    title: 'Clareza para cada interação com IA.',
    subtitle:
      'O Promptilia é um aprimorador de prompts para macOS. Selecione qualquer texto, toque no atalho e receba uma versão mais calma e clara do que você quis dizer.',
    description:
      'Cada reescrita respeita seus Perfis e Regras, então respostas no Gmail, tickets no Jira e briefings compartilham o mesmo tom, estrutura e clareza — sem sair do seu Mac.',
  },
  waitlist: {
    placeholder: 'voce@email.com',
    submit: 'Entrar na lista de espera',
    success: 'Obrigado! Em breve entraremos em contato.',
    error: 'Informe um e-mail válido para participar da lista.',
    label: 'Endereço de e-mail',
  },
  likeThanks: 'Obrigado pelo carinho',
  ctaSecondary: 'Gostar do Promptilia',
  languageToggleLabel: 'Selecionar idioma',
  featureDetailLabel: 'Saiba mais sobre {title}',
  status: {
    before: 'Antes',
    enhancing: 'Aprimorando…',
    ready: 'Pronto',
    enhancingMessage: 'Aprimorando o prompt e combinando perfis e regras…',
    enhancedMessage: 'Prompt aprimorado disponível',
    enhanceButton: 'Aprimorar prompt',
    afterLabel: 'Depois do Promptilia',
  },
  features: [
    {
      title: 'Estrutura acolhedora',
      description: 'Limpo, composto e gentil — do texto cru para um briefing sereno.',
    },
    {
      title: 'Confiança local',
      description: 'Tudo roda no seu Mac. Sem servidores, sem registros, apenas clareza.',
    },
    {
      title: 'Velocidade silenciosa',
      description: 'Um atalho captura, aprimora e devolve uma resposta constante.',
    },
  ],
  detailedFeatures: [
    {
      title: 'Motor de Clareza',
      icon: '✨',
      description: 'Transforma qualquer texto em um prompt estruturado e objetivo.',
      detail:
        'Limpa parágrafos confusos, adiciona estrutura e mantém o tom calmo para que cada prompt soe como você imaginou.',
    },
    {
      title: 'Perfis',
      icon: '🪄',
      description: 'Defina tom, estilo e comportamento para cada reescrita.',
      detail:
        'Capture sua voz pessoal — briefing acolhedor, nota de produto, atualização executiva — e reutilize instantaneamente.',
    },
    {
      title: 'Regras',
      icon: '📐',
      description: 'Formatação e estrutura consistentes em todo resultado.',
      detail:
        'Configure listas, cabeçalhos, limites de caracteres ou resumos para manter cada prompt obediente.',
    },
    {
      title: 'Feitiços',
      icon: '🎯',
      description: 'Micro-prompts reutilizáveis para edições e transformações comuns.',
      detail:
        'Construa uma paleta de automações — suavizar, resumir para executivos, destacar riscos — e invoque quando quiser.',
    },
    {
      title: 'Consciência de contexto',
      icon: '🧠',
      description: 'Adapta tom e estrutura ao app em que você está trabalhando.',
      detail:
        'O Promptilia percebe se você está em e-mail, Jira ou notas e ajusta a reescrita automaticamente.',
    },
    {
      title: 'Privacidade local',
      icon: '🔒',
      description: 'Processamento e armazenamento locais — sem contas ou nuvem.',
      detail:
        'Seus prompts, perfis e histórico nunca saem da máquina, mantendo trabalhos sensíveis auditáveis.',
    },
    {
      title: 'Fluxo via área de transferência',
      icon: '⌘',
      description: 'Capture, aprimore e cole texto melhorado com um atalho.',
      detail:
        'Selecione, pressione o atalho e cole — o Promptilia devolve a versão refinada em um gesto.',
    },
    {
      title: 'Histórico',
      icon: '🗂️',
      description: 'Revise, reutilize ou itere sobre cada prompt aprimorado.',
      detail:
        'Percorra reescritas anteriores, duplique ou execute novamente com novo contexto quando precisar.',
    },
  ],
  typewriter: `## Pedido detalhado e aprimorado\nA seção de regras globais no Promptilia parece desorganizada. Gostaria que ela fosse reformulada seguindo a mesma estrutura da seção de perfis. Além disso, cada perfil precisa ter sua própria subseção. Na tela de configurações, desejo adicionar uma opção para personalizar os atalhos de entrada e de colagem, incluindo os ícones dos modificadores do macOS para melhorar a usabilidade.\n\n## Tarefas\n- Reestruturar a seção de regras globais para seguir o padrão dos perfis.\n- Criar subseções aninhadas para cada perfil dentro das regras globais.\n- Adicionar uma configuração para personalizar os atalhos de entrada e colagem.\n- Incluir ícones dos modificadores do macOS na tela de configurações.\n- Garantir que o novo layout seja acessível e fácil de navegar.\n- Testar toda a implementação para funcionalidade e usabilidade.\n\n## Resumo\nPapel: Engenheiro(a) de software orientado(a) a produto, UX e design — garantindo soluções bem arquitetadas, seguras e focadas na privacidade. | Objetivo: Refinar a seção de regras globais e aprimorar a tela de configurações para personalizar atalhos. | Instruções: Manter clareza e consistência; ✓ organizar seções de forma lógica; + usar ícones para facilitar o entendimento; ✓ validar cada entrada do usuário e comunicar erros; 📝 registrar pressupostos e limites de implementação.`,
  samplePrompt:
    'Este é o Promptilia e ele tem uma seção de "regras globais", mas ainda está esquisita. Quero que siga o mesmo padrão dos Perfis. Além disso, cada perfil precisa ter sua própria subseção. Na tela de configurações, preciso adicionar uma opção para alterar os atalhos de captura e colagem, com os ícones dos modificadores do macOS.',
  sections: {
    howItWorksEyebrow: 'Como funciona',
    howItWorksTitle: 'Um companheiro para macOS presente em qualquer fluxo.',
    howItWorksDescription:
      'O Promptilia vive no seu Mac. Selecione qualquer texto, use o atalho e ele captura, aprimora e devolve uma versão serena usando seus Perfis e Regras.',
    storyEyebrow: 'Como funciona',
    storyTitle: 'Por que o Promptilia mantém os prompts consistentes',
    storyDescription:
      'O Promptilia fica ativo o tempo todo. Selecione um e-mail, briefing ou ticket, pressione o atalho e receba um prompt mais calmo, rico em palavras-chave, sem interromper seu fluxo.',
    productEyebrow: 'Produto',
    productTitle: 'Feito para foco.',
    productDescription:
      'Os elementos essenciais que mantêm o Promptilia firme: Perfis para definir tom, Regras para controlar a entrega, captura via área de transferência para agilidade e privacidade local para conformidade.',
    clipboardTitle: 'O Promptilia está chegando.',
    clipboardDescription: 'Entre na lista para ser avisado quando a primeira versão para macOS estiver disponível.',
    finalEyebrow: 'Como funciona',
    finalTitle: 'Um companheiro para macOS presente em qualquer fluxo.',
    finalDescription: 'O Promptilia vive no seu Mac. Selecionar, aprimorar, colar, repetir.',
  },
  steps: [
    {
      title: '1. Capturar',
      body: 'Realce qualquer texto no macOS e pressione o atalho. O Promptilia captura o conteúdo sem trocar de aplicativo.',
    },
    {
      title: '2. Transformar',
      body: 'Perfis e Regras determinam tom, estrutura e formato antes da IA devolver o texto.',
    },
    {
      title: '3. Devolver',
      body: 'Em segundos você recebe uma versão polida, pronta para colar, substituir ou continuar refinando.',
    },
  ],
  navigation: {
    links: [
      { label: 'Recursos', href: '/features' },
      { label: 'Preços', href: '/pricing' },
      { label: 'Sobre', href: '/about' },
      { label: 'Contato', href: '/contact' },
    ],
    downloadLabel: 'Baixar',
  },
  footer: {
    description:
      'Transforme seus prompts com aprimoramento por IA, organização inteligente e integração fluida ao fluxo de trabalho. Construído por desenvolvedores, para desenvolvedores.',
    productHeading: 'Produto',
    companyHeading: 'Empresa',
    note: '© 2025 Promptilia. Todos os direitos reservados.',
    productLinks: [
      { label: 'Recursos', href: '/features' },
      { label: 'Preços', href: '/pricing' },
      { label: 'Download', href: '#' },
    ],
    companyLinks: [
      { label: 'Sobre', href: '/about' },
      { label: 'Contato', href: '/contact' },
      { label: 'Privacidade', href: '#' },
      { label: 'Termos', href: '#' },
    ],
  },
};

const content: Record<SupportedLocale, LocaleContent> = {
  en,
  'pt-br': ptBr,
};

export default content;
